import React from 'react';
import { Box, Typography, Stack, Paper, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import GridHistoryIcon from '../icons/GridHistoryIcon';

// Mobile-First Styled Components
const HistoryContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    gap: '16px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '26px',
  },
}));

const HistoryTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: '#121927',
  [theme.breakpoints.up('sm')]: {
    fontSize: '22px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '26px',
  },
}));

// Desktop Table Components
const HistoryTable = styled(Paper)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'block',
    borderRadius: '12px',
    border: '0.6px solid #F6F0F0',
    backgroundColor: '#ffffff',
    boxShadow: 'none',
    overflow: 'hidden',
    width: '100%',
  },
}));

const TableHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  padding: '16px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: '91px',
  minWidth: '900px',
  [theme.breakpoints.down('lg')]: {
    gap: '60px',
    minWidth: '800px',
  },
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#ffffff',
  minWidth: '80px',
}));

const TableRow = styled(Box)(({ theme }) => ({
  padding: '16px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: '91px',
  borderBottom: '1px solid #F6F0F0',
  minWidth: '900px',
  [theme.breakpoints.down('lg')]: {
    gap: '60px',
    minWidth: '800px',
  },
}));

const CellText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#000000',
  minWidth: '80px',
}));

const GridIconContainer = styled(Box)(({ theme }) => ({
  width: '42px',
  height: '42px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Mobile Card Components
const MobileCardsContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const MobileHistoryCard = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  overflow: 'hidden',
  width: '100%',
}));

const MobileCardHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const MobileCardTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#ffffff',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
}));

const MobileCardDate = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.9)',
  flexShrink: 0,
  marginLeft: '12px',
}));

const MobileCardBody = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
}));

const MobileCardGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '12px',
}));

const MobileCardItem = styled(Stack)(({ theme }) => ({
  gap: '2px',
}));

const MobileCardLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
}));

const MobileCardValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927',
}));

const MobileCardIconRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '1px solid #E2E8F0',
}));

const HeatmapHistorySection = ({ history }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <HistoryContainer>
      <HistoryTitle>Heat Map History</HistoryTitle>

      {/* Mobile Cards View */}
      <MobileCardsContainer>
        {history.map((item, index) => (
          <MobileHistoryCard key={index} elevation={0}>
            <MobileCardHeader>
              <MobileCardTitle>{item.keyword}</MobileCardTitle>
              <MobileCardDate>{item.date}</MobileCardDate>
            </MobileCardHeader>
            <MobileCardBody>
              <MobileCardGrid>
                <MobileCardItem>
                  <MobileCardLabel>Grid Size</MobileCardLabel>
                  <MobileCardValue>{item.gridSize}</MobileCardValue>
                </MobileCardItem>
                <MobileCardItem>
                  <MobileCardLabel>Radius</MobileCardLabel>
                  <MobileCardValue>{item.radius}</MobileCardValue>
                </MobileCardItem>
                <MobileCardItem>
                  <MobileCardLabel>Avg Rank</MobileCardLabel>
                  <MobileCardValue sx={{ color: '#0B91D6', fontWeight: 600 }}>
                    {item.averageRank || '—'}
                  </MobileCardValue>
                </MobileCardItem>
                <MobileCardItem>
                  <MobileCardLabel>Listing</MobileCardLabel>
                  <MobileCardValue sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.listing}
                  </MobileCardValue>
                </MobileCardItem>
              </MobileCardGrid>
              <MobileCardIconRow>
                <GridHistoryIcon width={32} height={32} />
                <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                  {item.gridSize} Grid Analysis
                </Typography>
              </MobileCardIconRow>
            </MobileCardBody>
          </MobileHistoryCard>
        ))}
      </MobileCardsContainer>

      {/* Desktop Table View */}
      <HistoryTable>
        <Box sx={{ overflowX: 'auto' }}>
          <TableHeader>
            <HeaderText>Grid</HeaderText>
            <HeaderText>Grid size</HeaderText>
            <HeaderText>Keyword</HeaderText>
            <HeaderText>Radius</HeaderText>
            <HeaderText>Listing</HeaderText>
            <HeaderText>Average Rank</HeaderText>
            <HeaderText>Date</HeaderText>
          </TableHeader>

          {history.map((item, index) => (
            <TableRow key={index}>
              <GridIconContainer>
                <GridHistoryIcon width={42} height={42} />
              </GridIconContainer>
              <CellText>{item.gridSize}</CellText>
              <CellText>{item.keyword}</CellText>
              <CellText>{item.radius}</CellText>
              <CellText>{item.listing}</CellText>
              <CellText>{item.averageRank}</CellText>
              <CellText>{item.date}</CellText>
            </TableRow>
          ))}
        </Box>
      </HistoryTable>
    </HistoryContainer>
  );
};

export default HeatmapHistorySection;